/******************************************************************************
 * Actions
 ******************************************************************************/

// Definitions used in action objects
import {
    ColorScheme,
    DeleteSessionAction,
    HistoryDataTypeEnum,
    InteractionType,
    QueryLanguage,
    SaveSessionAction,
    Session,
    SetColumnFavStateAction,
    SetComparisonWeightAction,
    SetDatasourceAction,
    SetFavoriteWeightAction,
    SetInterpretationsLimitAction,
    SetQueryAction,
    SetQueryLanguageAction,
    SetQueryWeightAction,
    SetRelevanceWeightAction,
    SetRowsPerTableLimitAction,
    SetTableFavStateAction,
    SetThemeAction,
    SetUserIdAction,
    ToggleAppDrawerVisibilityAction,
    ToggleColumnFavStateAction,
    ToggleTableFavStateAction,
    UiStateActionTypeIdentifiers,
} from "#store/ui";
import { action as createAction } from "@iva/store-utils";
import { GetState, isDone, ThunkResult } from "#store/types";
import { Dispatch } from "redux";
import { v4 } from "uuid";
import { logUidUpdate } from "#store/logging";
import { stringToEnumValue } from "#/util/enums";
import { DatasourceDto, DatasourceEnum } from "#services/pilotService/types";
import { isPresent, Optional } from "#types";
import { Interpretation, TableDto } from "@inode/pilot-client-typescript-fetch";
import { isInterpretation } from "#/util/typeUtils";
import { debounce } from "lodash";

export function setUserId(userId: string): SetUserIdAction {
    return createAction(UiStateActionTypeIdentifiers.SET_USER_ID, {
        value: userId,
    });
}

export function generateNewUserId(): ThunkResult {
    return (dispatch: Dispatch) => {
        const newId = v4();
        dispatch(logUidUpdate(newId));
        dispatch(setUserId(newId));
    };
}

export function setTheme(type: ColorScheme): SetThemeAction {
    return createAction(UiStateActionTypeIdentifiers.SET_THEME, type);
}

export function toggleAppDrawer(): ToggleAppDrawerVisibilityAction {
    return createAction(UiStateActionTypeIdentifiers.TOGGLE_DRAWER, undefined);
}

export function setInterpretationsLimit(
    value: number
): SetInterpretationsLimitAction {
    return createAction(
        UiStateActionTypeIdentifiers.SET_INTERPRETATIONS_LIMIT,
        value
    );
}

export function setMaxResultsPerTable(
    value: number
): SetRowsPerTableLimitAction {
    return createAction(
        UiStateActionTypeIdentifiers.SET_ROWS_PER_TABLE_LIMIT,
        value
    );
}

export function setInterpretationFav(
    interpretationId: number,
    value: boolean
): SetTableFavStateAction {
    return createAction(UiStateActionTypeIdentifiers.SET_TABLE_FAV_STATE, {
        id: interpretationId,
        value,
    });
}

export function toggleInterpretationFav(
    interpretationId: number
): ToggleTableFavStateAction {
    return createAction(
        UiStateActionTypeIdentifiers.TOGGLE_TABLE_FAV_STATE,
        interpretationId
    );
}

export function toggleColumnFav(
    columnName: string
): ToggleColumnFavStateAction {
    return createAction(
        UiStateActionTypeIdentifiers.TOGGLE_COLUMN_FAV_STATE,
        columnName
    );
}

export function setColumnFav(
    columnName: string,
    value: boolean
): SetColumnFavStateAction {
    return createAction(UiStateActionTypeIdentifiers.SET_COLUMN_FAV_STATE, {
        column: columnName,
        value: value,
    });
}

export function setRelevanceWeight(weight: number): SetRelevanceWeightAction {
    return createAction(
        UiStateActionTypeIdentifiers.SET_RELEVANCE_WEIGHT,
        weight
    );
}

export function setComparisonWeight(weight: number): SetComparisonWeightAction {
    return createAction(
        UiStateActionTypeIdentifiers.SET_COMPARISON_WEIGHT,
        weight
    );
}

export function setQueryWeight(weight: number): SetQueryWeightAction {
    return createAction(UiStateActionTypeIdentifiers.SET_QUERY_WEIGHT, weight);
}

export function setFavoriteWeight(weight: number): SetFavoriteWeightAction {
    return createAction(
        UiStateActionTypeIdentifiers.SET_FAVORITE_WEIGHT,
        weight
    );
}

export function setQuery(
    query: string,
    opts: { immediate: boolean } = { immediate: false }
): ThunkResult {
    return (dispatch: Dispatch, getState: GetState) => {
        if (opts.immediate) {
            dispatch(setQueryActionInternal(query));
        } else {
            setQueryDebounced(dispatch, getState, query);
        }
    };
}

function setQueryActionInternal(query: string): SetQueryAction {
    return createAction(UiStateActionTypeIdentifiers.SET_QUERY_STRING, query);
}

export const setQueryDebounced = debounce(
    (dispatch: Dispatch, getState: GetState, query: string) => {
        dispatch(setQueryActionInternal(query));
    },
    250
);

export function setQueryLanguage(
    language: QueryLanguage
): SetQueryLanguageAction {
    return createAction(
        UiStateActionTypeIdentifiers.SET_QUERY_LANGUAGE,
        language
    );
}

export function setDatasource(source: string): ThunkResult {
    return (dispatch, getState) => {
        const converted = stringToEnumValue<
            typeof DatasourceEnum,
            DatasourceEnum
        >(DatasourceEnum, source);
        if (!converted) {
            console.warn(
                `trying to set datasource to illegal value ${source}, ignoring it.`
            );
        }

        const ioState = getState().datasources.data;

        if (isDone(ioState) && isPresent(isPresent(ioState))) {
            const find = ioState.payload.find(x => x.name === converted);
            if (find) {
                dispatch(_setDatasource(find));
            }
        }
    };
}

export function addHistory(
    element: Optional<Interpretation | TableDto>,
    interactionType: InteractionType
): ThunkResult {
    return (dispatch, getState) => {
        if (!isPresent(element)) {
            return;
        }
        dispatch(
            createAction(UiStateActionTypeIdentifiers.ADD_HISTORY_ITEM, {
                id: element.id,
                type: inferDatatype(element),
                actionType: interactionType,
                timestamp: new Date().getTime(),
            })
        );
    };
}

export function addHistories(
    elements: Interpretation[] | TableDto[],
    interactionType: InteractionType
): ThunkResult {
    return (dispatch, getState) => {
        if (elements.length === 0) {
            return;
        }
        const timestamp = new Date().getTime();
        dispatch(
            createAction(
                UiStateActionTypeIdentifiers.ADD_HISTORY_ITEMS,
                elements.map(element => {
                    return {
                        id: element.id,
                        type: inferDatatype(element),
                        actionType: interactionType,
                        timestamp,
                    };
                })
            )
        );
    };
}

function _setDatasource(source: DatasourceDto): SetDatasourceAction {
    return createAction(UiStateActionTypeIdentifiers.SET_DATASOURCE, source);
}

function inferDatatype(element: any): HistoryDataTypeEnum {
    if (isInterpretation(element)) {
        return HistoryDataTypeEnum.INTERPRETATION;
    } else {
        return HistoryDataTypeEnum.UNKNOWN;
    }
}

export function saveSession(session: Session): SaveSessionAction {
    return createAction(UiStateActionTypeIdentifiers.SAVE_SESSION, session);
}

export function loadSession(sessionName: string): ThunkResult {
    return (dispatch, getState) => {
        const session = getState().uiState.savedSessions.find(
            x => x.label === sessionName
        );
        if (!session) {
            console.warn("Trying to load non-existent session. Ignoring...");
        } else {
            // remove all existing favs
            getState().uiState.activeInterpretations.forEach(i => {
                dispatch(setInterpretationFav(i, false));
            });

            session.interpretations.forEach(x => {
                dispatch(setInterpretationFav(x, true));
            });
        }
    };
}

export function deleteSession(sessionName: string): DeleteSessionAction {
    return createAction(
        UiStateActionTypeIdentifiers.DELETE_SESSION,
        sessionName
    );
}
